import { ReactNode, useCallback, useContext, useMemo, useRef } from "react";
import { Task } from "@common/types/task";
import { pluralizeWithCount } from "@common/helpers/string";
import { omitBy } from "@common/helpers/objects";
import {
    TransactionView,
    TransactionViewSchema,
} from "@common/types/views/transactionViews";
import { useIsAccrualLedgerEnabled } from "@hooks/useIsAccrualLedgerEnabled";
import { TablePageHeader } from "@components/pageHeader/TablePageHeader";
import { TablePageSubHeader } from "@design-system/TablePageSubHeader/TablePageSubHeader";
import { FilterTabs } from "@components/pageHeader/filters/lib";
import { PaginatedTransactionsTableContextProvider } from "@components/transactions/PaginatedTransactionsTableContextProvider";
import {
    PaginatedTransactionsActionsContext,
    PaginatedTransactionsDataContext,
} from "@components/transactions/PaginatedTransactionsTable.context";
import { TransactionsBulkActionsContextProvider } from "@components/transactions/TransactionsBulkActions/TransactionsBulkActionsContextProvider";
import {
    DEFAULT_FILTERS,
    FiltersChangeFn,
} from "@components/transactions/filters/lib";
import { TransactionsTaskView } from "../Transactions/TransactionsTaskView";
import { useTransactionViewConfig } from "@components/transactions/views/useTransactionViewConfig";
import { ColumnSettings } from "@design-system/ViewToolbar/ColumnSettings";
import { SCHEDULE_REVIEW_QUERY_KEY } from "@components/transactions/useTransactionsQuery";
import { LOAD_MORE_PAGINATION_MODE } from "@hooks/paginationMode";
import { ScheduleReviewOutcomeProvider } from "./ScheduleReviewOutcome.context";
import styles from "./ScheduleReviewTaskBody.module.scss";

export interface ScheduleReviewFooterOptions {
    scrollTo?: (options: ScrollToOptions) => void;
}

interface ScheduleReviewTransactionsTaskBodyProps {
    selectedTask: Task;
    buildDefaultView: (entityIds: number[]) => TransactionView;
    emptyText: string;
    renderFooter: (options?: ScheduleReviewFooterOptions) => ReactNode;
}

interface ContentProps extends ScheduleReviewTransactionsTaskBodyProps {
    defaultView: TransactionView;
}

/**
 * Shared shell of the transaction-based schedule review tasks (suspicious
 * transactions, rule-suggested schedules). Mirrors the accrual review task
 * body: the task's persisted view fixes entity and review filter, the user
 * can layer interactive filters on top, and the footer is supplied by the
 * task-specific action bar.
 */
const ScheduleReviewTransactionsTaskBodyContent = ({
    defaultView,
    emptyText,
    renderFooter,
}: ContentProps) => {
    const isAccrualLedgerFilterEnabled = useIsAccrualLedgerEnabled();
    const transactionsViewConfig = useTransactionViewConfig();
    const { transactionCount, filters, currentView } = useContext(
        PaginatedTransactionsDataContext,
    );
    const {
        setCurrentFilters,
        setPage,
        setShownTransactionId,
        setCurrentView,
    } = useContext(PaginatedTransactionsActionsContext);

    const currentFiltersRef = useRef(filters);
    currentFiltersRef.current = filters;

    const handleFiltersChange = useCallback<FiltersChangeFn>(
        (value) => {
            const merged = { ...currentFiltersRef.current, ...value };
            setPage(1);
            setShownTransactionId(undefined);
            setCurrentFilters(
                omitBy(merged, (v) => v === null || v === undefined),
            );
        },
        [setPage, setShownTransactionId, setCurrentFilters],
    );

    const availableFilters = useMemo(
        () => [
            FilterTabs.DATE,
            FilterTabs.CATEGORY,
            FilterTabs.ACCOUNT,
            FilterTabs.AMOUNT,
            FilterTabs.COUNTERPARTY,
            FilterTabs.NOTE,
            FilterTabs.LEDGER,
            ...(isAccrualLedgerFilterEnabled
                ? [FilterTabs.ACCRUAL_LEDGER]
                : []),
            FilterTabs.TRANSACTION_LOCKED,
        ],
        [isAccrualLedgerFilterEnabled],
    );

    const viewWithSelection = useMemo<TransactionView>(
        () => ({
            ...currentView,
            options: {
                ...currentView.options,
                bulkActions: true,
                selection: true,
            },
        }),
        [currentView],
    );

    const handleViewChange = useCallback(
        (view: TransactionView) => setCurrentView(view),
        [setCurrentView],
    );

    return (
        <TransactionsBulkActionsContextProvider
            currentFilters={filters}
            totalTransactions={transactionCount}
            onUpdated={() => {}}
        >
            <div className={styles.container}>
                {transactionCount > 0 && (
                    <TablePageSubHeader borderBottom>
                        {pluralizeWithCount("transaction", transactionCount)} to
                        review
                    </TablePageSubHeader>
                )}
                <TablePageHeader
                    filters={filters}
                    onFiltersChange={handleFiltersChange}
                    defaultFilters={DEFAULT_FILTERS}
                    availableFilters={availableFilters}
                    hideClearButton
                    isFullPage
                >
                    <ColumnSettings<TransactionView>
                        view={viewWithSelection}
                        viewConfig={transactionsViewConfig}
                        defaultView={defaultView}
                        onViewChange={handleViewChange}
                    />
                </TablePageHeader>
                <section className={styles.body}>
                    <ScheduleReviewOutcomeProvider>
                        <TransactionsTaskView
                            view={viewWithSelection}
                            footer={renderFooter}
                            emptyView={() => <span>{emptyText}</span>}
                        />
                    </ScheduleReviewOutcomeProvider>
                </section>
            </div>
        </TransactionsBulkActionsContextProvider>
    );
};

export const ScheduleReviewTransactionsTaskBody = (
    props: ScheduleReviewTransactionsTaskBodyProps,
) => {
    const { selectedTask, buildDefaultView } = props;

    const defaultView = useMemo<TransactionView | null>(() => {
        if (selectedTask.view) {
            const parsed = TransactionViewSchema.safeParse(selectedTask.view);
            return parsed.success ? parsed.data : null;
        }
        return buildDefaultView(selectedTask.entityIds ?? []);
    }, [buildDefaultView, selectedTask.entityIds, selectedTask.view]);

    if (!defaultView) {
        return null;
    }

    return (
        <PaginatedTransactionsTableContextProvider
            defaultView={defaultView}
            paginationMode={LOAD_MORE_PAGINATION_MODE}
            queryKeyPrefix={SCHEDULE_REVIEW_QUERY_KEY}
            key={selectedTask.id}
        >
            <ScheduleReviewTransactionsTaskBodyContent
                {...props}
                defaultView={defaultView}
            />
        </PaginatedTransactionsTableContextProvider>
    );
};
